/**
 * Presentational only — the live container is OrganizationAdminSection.tsx.
 */

import type { InviteMode } from '@localagent/sdk';
import { Button } from '@localagent/ui/components/button';
import { Input } from '@localagent/ui/components/input';
import { Badge } from '@localagent/ui/components/badge';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';

export interface OrganizationInvitePolicyFormProps {
  mode: InviteMode;
  domains: string[];
  draftDomain: string;
  onModeChange: (mode: InviteMode) => void;
  onDraftDomainChange: (domain: string) => void;
  onAddDomain: () => void;
  onRemoveDomain: (domain: string) => void;
  onSave: () => void;
  isLoading?: boolean;
  isSaving?: boolean;
  isDirty?: boolean;
  error?: string | null;
  success?: string | null;
}

export function OrganizationInvitePolicyForm({
  mode,
  domains,
  draftDomain,
  onModeChange,
  onDraftDomainChange,
  onAddDomain,
  onRemoveDomain,
  onSave,
  isLoading = false,
  isSaving = false,
  isDirty = false,
  error = null,
  success = null,
}: OrganizationInvitePolicyFormProps) {
  const restricted = mode === 'RESTRICTED';
  const needsADomain = restricted && domains.length === 0;
  const busy = isLoading || isSaving;

  if (isLoading) {
    return (
      <div className="rounded-lg bg-card p-4" data-testid="invite-policy-loading">
        <LoadingState label="Loading invite policy…" />
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-card p-4 space-y-3" data-testid="invite-policy-form">
      <div>
        <h3 className="text-label font-medium">Who can be invited</h3>
        <p className="text-caption text-muted-foreground">
          Applies to every invitation sent from this organization. Nobody joins
          without an invitation either way.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          type="button"
          variant={restricted ? 'outline' : 'default'}
          aria-pressed={!restricted}
          disabled={busy}
          data-testid="invite-policy-mode-anyone"
          onClick={() => onModeChange('ALL_ALLOWED')}
        >
          Anyone
        </Button>
        <Button
          size="sm"
          type="button"
          variant={restricted ? 'default' : 'outline'}
          aria-pressed={restricted}
          disabled={busy}
          data-testid="invite-policy-mode-restricted"
          onClick={() => onModeChange('RESTRICTED')}
        >
          Only these domains
        </Button>
      </div>

      <div className="space-y-2">
        <div className="flex items-baseline gap-2">
          <span className="text-label font-medium text-muted-foreground">Allowed domains</span>
          {!restricted ? (
            <span className="text-caption text-muted-foreground" data-testid="invite-policy-domains-inactive">
              not applied while anyone can be invited
            </span>
          ) : null}
        </div>

        {domains.length > 0 ? (
          <ul className="flex flex-wrap gap-2" data-testid="invite-policy-domains">
            {domains.map((domain) => (
              <li key={domain}>
                <Badge variant="outline" className={restricted ? undefined : 'opacity-50'}>
                  {domain}
                  {restricted ? (
                    <button
                      type="button"
                      className="ml-1.5 text-muted-foreground hover:text-foreground"
                      aria-label={`Remove ${domain}`}
                      disabled={busy}
                      data-testid={`invite-policy-remove-${domain}`}
                      onClick={() => onRemoveDomain(domain)}
                    >
                      ×
                    </button>
                  ) : null}
                </Badge>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-caption text-muted-foreground" data-testid="invite-policy-domains-empty">
            No domains configured.
          </p>
        )}

        {restricted ? (
          <form
            className="flex flex-wrap items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              onAddDomain();
            }}
          >
            <Input
              className="w-auto grow min-w-[12rem]"
              value={draftDomain}
              disabled={busy}
              placeholder="company.com"
              aria-label="Domain to allow"
              data-testid="invite-policy-domain-input"
              onChange={(e) => onDraftDomainChange(e.target.value)}
            />
            <Button
              size="sm"
              type="submit"
              variant="outline"
              disabled={busy || !draftDomain.trim()}
              data-testid="invite-policy-domain-add"
            >
              Add
            </Button>
          </form>
        ) : null}

        {restricted ? (
          <p className="text-caption text-muted-foreground">
            Stytch rejects common consumer domains such as gmail.com.
          </p>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          size="sm"
          type="button"
          disabled={busy || !isDirty || needsADomain}
          data-testid="invite-policy-save"
          onClick={onSave}
        >
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
        {needsADomain ? (
          <span className="text-caption text-muted-foreground" data-testid="invite-policy-needs-domain">
            Needs at least one domain — or switch to Anyone.
          </span>
        ) : null}
      </div>

      {error ? (
        <p className="text-body text-destructive" role="alert" data-testid="invite-policy-error">
          {error}
        </p>
      ) : null}
      {success ? (
        <p className="text-body text-success-text" data-testid="invite-policy-success">
          {success}
        </p>
      ) : null}
    </div>
  );
}
